/**
 * ResignationWizardController — wizard 4 modales §25.B.
 *
 * Orchestre la séquence :
 *   1. Motif (radio + textarea libre si « autre »)
 *   2. Checklist en-cours (lecture seule, lue depuis getLifecycle)
 *   3. Choix d'archivage gratuit A (en ligne 10 ans) ou B (export ZIP 90 j)
 *   4. Récap + case à cocher obligatoire + bouton confirmer
 *
 * Comportements spécifiques :
 *   - Motif `cessation` → redirige immédiatement vers /cessation-inpi.html
 *     (sortie du wizard, cf. §25.C).
 *   - Submit final → POST `/organization/lifecycle/resignation` puis
 *     reload pour afficher le bandeau résiliation programmée.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class ResignationWizardController extends BaseController {
  static targets = [
    "dialog",
    "step",
    "motifRadio",
    "motifOtherInput",
    "motifContinueBtn",
    "recurrencesSlot",
    "facturesImpayeesSlot",
    "devisAcceptesSlot",
    "declarationsSlot",
    "mandatsSlot",
    "documentsSlot",
    "checklistEmpty",
    "archiveOption",
    "recapMotif",
    "recapDateEffet",
    "recapArchiveOption",
    "recapEnCours",
    "confirmCheckbox",
    "confirmBtn",
  ];

  connect() {
    this._currentStep = 1;
    this._lifecycle = null;
    this._submitting = false;
  }

  async open(event) {
    if (event) event.preventDefault();
    this._currentStep = 1;
    this._resetForm();
    if (this.hasDialogTarget && typeof this.dialogTarget.showModal === "function") {
      this.dialogTarget.showModal();
    }
    this._showStep(1);
    await this._loadLifecycle();
  }

  close(event) {
    if (event) event.preventDefault();
    if (this.hasDialogTarget && typeof this.dialogTarget.close === "function") {
      this.dialogTarget.close();
    }
  }

  onMotifChange() {
    const selected = this._selectedMotif();
    if (selected === "cessation") {
      window.location.assign("/cessation-inpi.html");
      return;
    }
    this._updateMotifContinueState();
  }

  onMotifOtherInput() {
    this._updateMotifContinueState();
  }

  onArchiveChange() {
    // Option A pré-sélectionnée, B reste valide ; pas de blocage spécifique.
  }

  onConfirmCheckboxChange() {
    if (!this.hasConfirmBtnTarget) return;
    this.confirmBtnTarget.disabled = !this._isConfirmChecked();
    this.confirmBtnTarget.setAttribute(
      "aria-disabled",
      this._isConfirmChecked() ? "false" : "true",
    );
  }

  next(event) {
    if (event) event.preventDefault();
    if (this._currentStep === 1 && !this._isMotifValid()) {
      this._showError("Choisissez un motif pour continuer.");
      return;
    }
    if (this._currentStep < 4) {
      this._currentStep += 1;
      if (this._currentStep === 4) {
        this._fillRecap();
      }
      this._showStep(this._currentStep);
    }
  }

  prev(event) {
    if (event) event.preventDefault();
    if (this._currentStep > 1) {
      this._currentStep -= 1;
      this._showStep(this._currentStep);
    }
  }

  async submit(event) {
    if (event) event.preventDefault();
    if (this._submitting) return;
    if (!this._isConfirmChecked()) {
      this._showError("Cochez la case de confirmation pour finaliser.");
      return;
    }

    const motive = this._selectedMotif();
    const motiveOther = motive === "autre" && this.hasMotifOtherInputTarget
      ? this.motifOtherInputTarget.value.trim()
      : null;
    const archiveChoice = this._selectedArchiveOption();

    this._submitting = true;
    if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = true;

    try {
      await api.initiateResignation({ motive, motiveOther, archiveChoice });
      this.close();
      this.showSuccess("Résiliation programmée. Vous pouvez l'annuler à tout moment depuis le bandeau.");
      window.location.reload();
    } catch (error) {
      const message = error && error.message
        ? error.message
        : "Impossible de programmer la résiliation.";
      this._showError(message);
    } finally {
      this._submitting = false;
      if (this.hasConfirmBtnTarget) {
        this.confirmBtnTarget.disabled = !this._isConfirmChecked();
      }
    }
  }

  async _loadLifecycle() {
    try {
      this._lifecycle = await api.getLifecycle();
      this._fillChecklist(this._lifecycle?.checklistItems ?? null);
    } catch (error) {
      this._showError(
        error && error.message
          ? error.message
          : "Impossible de charger l'état du compte.",
      );
    }
  }

  _showStep(stepNumber) {
    if (!this.hasStepTarget) return;
    this.stepTargets.forEach((stepEl) => {
      const idx = parseInt(stepEl.dataset.step ?? "0", 10);
      stepEl.hidden = idx !== stepNumber;
    });
  }

  _resetForm() {
    if (this.hasMotifRadioTarget) {
      this.motifRadioTargets.forEach((radio) => {
        radio.checked = radio.value === "autre";
      });
    }
    if (this.hasMotifOtherInputTarget) {
      this.motifOtherInputTarget.value = "";
    }
    if (this.hasArchiveOptionTarget) {
      this.archiveOptionTargets.forEach((radio) => {
        radio.checked = radio.value === "online";
      });
    }
    if (this.hasConfirmCheckboxTarget) {
      this.confirmCheckboxTarget.checked = false;
    }
    if (this.hasConfirmBtnTarget) {
      this.confirmBtnTarget.disabled = true;
      this.confirmBtnTarget.setAttribute("aria-disabled", "true");
    }
    this._updateMotifContinueState();
  }

  _updateMotifContinueState() {
    if (!this.hasMotifContinueBtnTarget) return;
    this.motifContinueBtnTarget.disabled = !this._isMotifValid();
  }

  _isMotifValid() {
    const motive = this._selectedMotif();
    if (motive === null) return false;
    if (motive === "autre") {
      if (!this.hasMotifOtherInputTarget) return false;
      return this.motifOtherInputTarget.value.trim().length > 0;
    }
    return true;
  }

  _isConfirmChecked() {
    return this.hasConfirmCheckboxTarget && this.confirmCheckboxTarget.checked;
  }

  _selectedMotif() {
    if (!this.hasMotifRadioTarget) return null;
    const selected = this.motifRadioTargets.find((r) => r.checked);
    return selected ? selected.value : null;
  }

  _selectedArchiveOption() {
    if (!this.hasArchiveOptionTarget) return "online";
    const selected = this.archiveOptionTargets.find((r) => r.checked);
    return selected ? selected.value : "online";
  }

  _fillChecklist(items) {
    if (!items || typeof items !== "object") {
      if (this.hasChecklistEmptyTarget) this.checklistEmptyTarget.hidden = false;
      return;
    }
    // EPIC-099 / 99.37 — `activeSepaMandates` retiré (mandat SEPA URSSAF
    // hors périmètre Notamicro). 6 catégories.
    this._setSlot(this.recurrencesSlotTarget, items.recurrences, "récurrence", "récurrences");
    this._setSlot(this.facturesImpayeesSlotTarget, items.unpaidInvoices, "facture", "factures");
    this._setSlot(this.devisAcceptesSlotTarget, items.acceptedQuotes, "devis", "devis");
    this._setSlot(this.declarationsSlotTarget, items.pendingDeclarations, "déclaration", "déclarations");
    this._setSlot(this.mandatsSlotTarget, items.activeMandates, "mandat", "mandats");
    this._setSlot(this.documentsSlotTarget, items.publicDocuments, "document", "documents");

    const total = [
      items.recurrences,
      items.unpaidInvoices,
      items.acceptedQuotes,
      items.pendingDeclarations,
      items.activeMandates,
    ]
      .map((n) => (typeof n === "number" ? n : 0))
      .reduce((a, b) => a + b, 0);
    if (this.hasChecklistEmptyTarget) {
      this.checklistEmptyTarget.hidden = total > 0;
    }
  }

  _setSlot(el, count, singular, plural) {
    if (!el) return;
    const safe = typeof count === "number" ? count : 0;
    const label = safe <= 1 ? singular : plural;
    el.textContent = safe === 0 ? `Aucun ${singular}` : `${safe} ${label}`;
  }

  _fillRecap() {
    const motive = this._selectedMotif();
    const labels = {
      besoins: "Notamicro ne correspond plus à mes besoins",
      tarifs: "Tarifs trop élevés",
      concurrent: "Passage à un autre logiciel",
      autre: "Autre motif",
    };
    if (this.hasRecapMotifTarget) {
      let motifLabel = labels[motive] ?? "—";
      if (motive === "autre" && this.hasMotifOtherInputTarget) {
        const txt = this.motifOtherInputTarget.value.trim();
        if (txt.length > 0) motifLabel = `Autre motif — ${txt}`;
      }
      this.recapMotifTarget.textContent = motifLabel;
    }
    if (this.hasRecapDateEffetTarget) {
      const computed = this._lifecycle?.computedResiliationDate ?? null;
      this.recapDateEffetTarget.textContent = computed
        ? this.formatDate(computed)
        : "Fin de la période en cours";
    }
    if (this.hasRecapArchiveOptionTarget) {
      const archive = this._selectedArchiveOption();
      this.recapArchiveOptionTarget.textContent = archive === "export_only"
        ? "Export ZIP seul, portail désactivé"
        : "Archivage en ligne 10 ans";
    }
    if (this.hasRecapEnCoursTarget) {
      const items = this._lifecycle?.checklistItems;
      const total = items
        ? [
            items.recurrences,
            items.unpaidInvoices,
            items.acceptedQuotes,
            items.pendingDeclarations,
            items.activeMandates,
          ]
            .map((n) => (typeof n === "number" ? n : 0))
            .reduce((a, b) => a + b, 0)
        : 0;
      this.recapEnCoursTarget.textContent = total === 0
        ? "Aucun en-cours"
        : `${total} en-cours non traités`;
    }
  }

  _showError(message) {
    this.showError(message);
  }
}
